import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../shared/store/auth.store";
import { UserTabLayout } from "./Layout";
import { hasStoredTokens } from "../shared/utils/storage";
import { storePendingToken } from "../shared/services/deep-link.service";

interface AuthLayoutProps {
  children?: ReactNode;
}

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const navigationInProgress = useRef(false);

  useEffect(() => {
    if (navigationInProgress.current) return;

    // ──────────────────────────────────────────────────────────────
    // Deep Link: сохраняем qrToken из URL перед редиректом на /login
    // Если в URL есть ?qrToken=..., сохраняем его в sessionStorage,
    // чтобы после авторизации LoginPage могла выполнить auto-scan.
    // ──────────────────────────────────────────────────────────────
    if (!hasStoredTokens()) {
      try {
        const searchParams = new URLSearchParams(location.search);
        const qrToken = searchParams.get("qrToken");
        if (qrToken) {
          console.log(
            "[AuthLayout] Preserving qrToken from URL before login redirect:",
            qrToken,
          );
          storePendingToken(qrToken);
        }
      } catch (err) {
        console.warn("[AuthLayout] Failed to preserve qrToken from URL:", err);
      }

      navigationInProgress.current = true;
      navigate("/login", { replace: true });
      return;
    }

    // Tokens exist but store says not authenticated — could validate with API.
    // For now, assume tokens are valid.
    if (!isAuthenticated) {
      const { setAuthenticated } = useAuthStore.getState();
      setAuthenticated(true);
    }
  }, [isAuthenticated, navigate, location]);

  if (!isAuthenticated) {
    // Render phase must never navigate — show a loading state only; the effect
    // above performs the redirect reliably.
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>⏳</p>
        <p style={{ color: "var(--second-color)" }}>Authenticating...</p>
      </div>
    );
  }

  return <UserTabLayout>{children}</UserTabLayout>;
};
